import { Notification } from 'components/Notification';
import { findDeep } from 'deepdash-es/standalone';
import _ from 'lodash';
import { filterVariants } from 'pages/documents/misc/_EditArea/EditAreaSidebar';
import { useGetBaseVariant } from 'pages/documents_v2/hooks/useGetBaseVariant';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { useIsPreviewMode } from 'pages/documents_v2/views/edit_area/hooks/useIsPreviewMode';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useContext, useMemo } from 'react';
import { CircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

export default function VariantViewButtons({ area, dirty = false }) {
    const { activeVariantId, setActiveVariantId } = useContext(AreaContext);
    const baseVariant = useGetBaseVariant();
    const { t } = useTranslation();
    const isPreviewMode = useIsPreviewMode();

    const variantEdits = area?.variantEdits ?? [];
    const baseVariantName = t('global:entities.baseVariant');

    const getVariantCount = (variantId) => {
        const localVariantEdits = HelperFunctions.getByValue(variantEdits, 'id', variantId);

        return localVariantEdits ? localVariantEdits.count : 0;
    };

    const variants = useMemo(() => {
        if (baseVariant === undefined || area === undefined) {
            return [];
        }

        const filtered = HelperFunctions.flatten(filterVariants(baseVariant.children, area.documentVariants));
        const localVariants = [];
        const baseVariantCount = getVariantCount(baseVariant.id);

        // Check if any of the variant have edits
        const variantsHaveEdits = filtered.some((variant) =>
            variant.parentId === null ? false : getVariantCount(variant.id) > 0,
        );

        localVariants.push({
            label: (
                <VariantSelectRow
                    name={baseVariantName}
                    checkCount={baseVariantCount}
                    showWarningIndicator={variantsHaveEdits}
                />
            ),
            value: baseVariant.id,
            variant: {
                prefix: '',
                name: baseVariantName,
            },
        });

        filtered
            .filter((_variant) => _variant.parentId === baseVariant.id || _variant.variantGroup)
            .forEach((_variant) => {
                if (_variant.variantGroup) {
                    localVariants.push({
                        label: <VariantSelectRow name={_variant.name} prefix={_variant.prefix} />,
                        options: filtered
                            .filter(
                                (_childVariant) =>
                                    _childVariant.parentId === _variant.id && _childVariant.variantGroup === false,
                            )
                            .map((_childVariant) => {
                                const childVariantCount = getVariantCount(_childVariant.id);

                                return {
                                    label: (
                                        <VariantSelectRow
                                            name={_childVariant.name}
                                            prefix={_childVariant.prefix}
                                            checkCount={childVariantCount}
                                        />
                                    ),
                                    value: _childVariant.id,
                                    variant: {
                                        prefix: _childVariant.prefix ?? '',
                                        name: _childVariant.name,
                                    },
                                };
                            }),
                    });
                } else {
                    const variantCount = getVariantCount(_variant.id);

                    localVariants.push({
                        label: (
                            <VariantSelectRow name={_variant.name} prefix={_variant.prefix} checkCount={variantCount} />
                        ),
                        value: _variant.id,
                        variant: {
                            prefix: _variant.prefix ?? '',
                            name: _variant.name,
                        },
                    });
                }
            });

        return localVariants;
    }, [baseVariant, area, variantEdits, baseVariantName]);

    if (area?.properties?.isTemplate === true) {
        return null;
    }

    return (
        <VariantViewButtonsSelect
            variants={variants}
            handleVariantChange={handleVariantChange}
            isPreviewMode={isPreviewMode}
            baseVariant={baseVariant}
        />
    );

    function handleVariantChange(newVariantId) {
        if (!newVariantId) {
            // select base variant
            setActiveVariantId(baseVariant.id);
            return;
        }

        if (activeVariantId === newVariantId) {
            return;
        }

        if (dirty) {
            HelperFunctions.confirmModal(
                t('global:confirm.confirmCancelChanges'),
                t('documents:document.navbar.main.editor.left.subnav.confirmSelection'),
                t('global:btn.cancel'),
                'danger',
            )
                .then(() => {
                    setActiveVariantId(newVariantId);
                })
                .catch(() => {});
        } else {
            setActiveVariantId(newVariantId);
        }
    }
}

function VariantSelectRow({ name, prefix = null, checkCount = 0, showWarningIndicator = false, className }) {
    return (
        <div className="d-flex align-items-center justify-content-between cursor-pointer area-editor-variant-item">
            <div>
                {!_.isNil(prefix) && <span className="text-muted">{prefix}&nbsp;&nbsp;</span>}
                <span className={className}>{name}</span>
            </div>

            {checkCount > 0 && <Notification count={checkCount} />}
            {checkCount === 0 && showWarningIndicator && <CircleFill className="text-danger" size={8} />}
        </div>
    );
}

function VariantViewButtonsSelect({ variants, handleVariantChange, isPreviewMode, baseVariant }) {
    const { activeVariantId } = useContext(AreaContext);
    const { t } = useTranslation();

    const filterOptions = ({ label, value, data }, input) => {
        if (_.isEmpty(input)) {
            return true;
        }

        const { variant } = data;
        const title = variant.prefix + variant.name;

        return title.toLowerCase().includes(input.toLowerCase());
    };

    const selectedOption = useMemo(() => {
        const option = findDeep(
            variants,
            (value) => {
                const option = value;

                if (option.hasOwnProperty('value') === false) {
                    return undefined;
                }

                return option.value === activeVariantId;
            },
            {
                childrenPath: 'options',
            },
        );

        if (option) {
            return option.value;
        }

        return undefined;
    }, [activeVariantId, variants]);

    return (
        <Select
            placeholder={t('documents:document.navbar.main.editor.left.subnav.selectVariant')}
            styles={customStyles}
            options={variants}
            value={selectedOption}
            onChange={(item, actionMeta) => {
                if (actionMeta.action === 'clear') {
                    handleVariantChange(baseVariant.id);
                    return;
                }

                handleVariantChange(item.value);
            }}
            className="variant-view edit-area-variant-select"
            classNamePrefix="variant-view"
            isClearable={isPreviewMode}
            filterOption={filterOptions}
            menuPortalTarget={document.body}
            theme={(theme) => {
                return {
                    ...theme,
                    borderRadius: 4,
                };
            }}
        />
    );
}

const customStyles = {
    groupHeading: (provided) => ({
        ...provided,
        color: '#255478',
        fontWeight: 'bold',
        paddingTop: 4,
        paddingBottom: 4,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 200 }),
};
